import React from 'react';
import {withTranslation} from 'react-i18next';
import {Field, Form as FinalForm} from 'react-final-form';
import {NotificationManager} from "../../components/common/react-notifications";
import {Col, Form, FormGroup, Input, InputGroup, InputGroupAddon, InputGroupText, Row} from 'reactstrap';
import {
  numberWithCommas,
  scientificToDecimal,
  selectStyles,
  stringToNumber
} from "../../helpers/Utils";
import LoaderButton from "../../components/LoaderButton";
import {composeValidators, mustBeNumber, required} from "../../helpers/Validators";
import PercentButtonGroup from "../../components/PercentButtonGroup";
import {application} from "../../constants/configs";
import {find, keyBy, min, max} from "lodash";
import OrderDetails from "./OrderDetails";
import OrderErrors from "./OrderErrors";
import {store} from "../../AppRenderer";
import {FORM_ERROR} from "final-form";
import Api from "../../services/Api";
import io from "socket.io-client";
import Select from "react-select";

class PlaceOrderForm extends React.Component {
  constructor(props) {
    super(props);
    const {pairs} = store.getState().general;
    this.pairOptions = pairs.map(pair => ({label: pair.localeName, value: pair.name}));
    this.sideOptions = [
      {label: props.t('Buy'), value: 'BUY'},
      {label: props.t("Sell"), value: 'SELL'},
    ];
    this.typeOptions = [
      {label: props.t('Limit Order'), value: 'LIMIT'},
      {label: props.t('Market Order'), value: 'MARKET'},
      {label: props.t('Stop-Limit Order'), value: 'STOP_LIMIT'},
    ]
    this.state = {
      orderbook: null,
      wallets: keyBy(props.wallets, 'token'),
      pairs: keyBy(pairs, 'name'),
    };
  }

  fixedToWithoutRound = (value, precision) => {
    if ((value || value === 0) && !isNaN(value)) {
      const number = scientificToDecimal(value);
      const regex = new RegExp(`^-?\\d+(?:.\\d{0,${precision}})?`);
      const result = number.match(regex)[0];
      return precision === 0 && result[result.length - 1] === '.'
        ? result.slice(0, result.length - 1) : result;
    }
    return null;
  };


  placeOrder = async (values, form) => {
    const {t, userId} = this.props;
    const {side, pairName, amount, type, price, stopPrice} = values;
    try {
      await Api.invoke('POST', '/admin-ui/user-orders', {
        userId,
        side,
        pair: pairName,
        fillType: 'NORMAL',
        amount: Number(amount),
        type: type !== 'STOP_LIMIT' ? type : 'LIMIT',
        price: type !== 'MARKET' ? Number(price) : undefined,
        ...(type !== 'LIMIT' && type !== 'MARKET' ? {conditions: [{price: Number(stopPrice)}]} : null),
      });
      NotificationManager.success(t('Order registered successfully.'), t('Message'));
      setTimeout(() => {form.restart();});
      this.afterSubmit();
    } catch (e) {
      if (e.fieldError) {
        return e.fieldError;
      }
      throw e;
    }
  };

  afterSubmit = async () => {
    const {afterSubmit, fetchWallets} = this.props;
    afterSubmit();
    const data = await fetchWallets();
    this.setState({
      wallets: keyBy(data, 'token'),
    })
  };

  validate = values => {
    const errors = {};
    const {t} = this.props;
    const {orderbook, wallets, pairs} = this.state;
    const {type, pairName, side, amount, price, details} = values;
    const pair = pairName ? pairs[pairName] : null;
    const locales = pair ? pair.localeName.split('/') : null;
    const tokens = pair ? pair.name.split('/') : null;
    const baseBalance = tokens ? wallets[tokens[0]].balance : null;
    const quoteBalance = tokens ? wallets[tokens[1]].balance : null;
    const balance = side === 'BUY' ? quoteBalance : baseBalance;
    const orders = orderbook ? orderbook.filter(order => order.side.toUpperCase() === side) : null;
    const bestPrice = orders && orders.length ? orders[0].price : null;
    if (type === 'MARKET' && details) {
      const value = side === 'BUY' ? details.totalPrice : Number(amount);
      if (value > Number(balance)) {
        errors.amount = t('Balance is not enough.');
      }
    }
    if (type === 'LIMIT' && price && Number(bestPrice)) {
      if (price < 0.5 * Number(bestPrice) || price > 1.5 * Number(bestPrice)) {
        errors.price = t('The difference between the order price and the best price is large.');
      }
    }
    if (type !== 'MARKET' && amount && price) {
      const value = side === 'BUY' ? Number(amount) * Number(price) : Number(amount);
      if (value > Number(balance)) {
        errors.amount = t('Balance is not enough.');
      }
      if (Number(amount) * Number(price) < Number(pair.minCost)) {
        errors[FORM_ERROR] = t('The total price should not be less than {{minCost}} {{token}}.', {
          minCost: numberWithCommas(Number(pair.minCost)),
          token: locales[1],
        });
      }
    }
    if (type === 'MARKET' && amount && details) {
      if (Number(amount) > 0 && (!details.computedAmount || details.computedAmount < Number(amount))) {
        errors[FORM_ERROR] = t('Orders not enough.')
      } else {
        if (details.totalPrice < Number(pair.minCost)) {
          errors[FORM_ERROR] = t('The total price should not be less than {{minCost}} {{token}}.', {
            minCost: numberWithCommas(Number(pair.minCost)),
            token: locales[1],
          });
        }
      }
    }
    return errors;
  };

  onSelectPair = (selected, input) => {
    if (input.value !== selected.value) {
      if (input.value) {
        this.wsUnsubscribe(input.value);
        setTimeout(() => {
          this.form.restart({
            side: 'BUY',
            type: 'LIMIT',
            pairName: selected.value,
          });
        });
      }
      input.onChange(selected.value);
      this.wsStart(selected.value);
    }
  };

  onSelectSide = selected => {
    setTimeout(() => {
      this.form.restart({
        side: selected.value,
        type: this.form.getFieldState('type').value,
        pairName: this.form.getFieldState('pairName').value,
      });
    });
  };

  wsStart = pairName => {
    this.socket = io(application.wsUrl, {
      secure: true,
      transports: ['websocket'],
      query: {
        pairName,
        lang: 'fa',
      },
    });
    this.socket.on('connect', () => {
      this.socket.emit('authenticate', { token: null });
    });
    this.wsSubscribe(pairName);
  };

  wsSubscribe = pairName => {
    this.socket.on(`${pairName}_orderbook`, data => {
      this.setState({
        orderbook: data,
      });
    });
    this.socket.on('pairs', data => {
      this.setState({
        pairs: keyBy(data, 'name'),
      });
    });
  };

  wsUnsubscribe = pairName => {
    this.setState({orderbook: null});
    this.socket.off(`${pairName}_orderbook`);
    this.socket.off('pairs');
    this.socket.disconnect();
  };

  componentWillUnmount() {
    if (this.socket && this.form) {
      this.wsUnsubscribe(this.form.getFieldState('pairName').value);
    }
  }

  stopPriceMustBeGreaterThan = limit => value =>
    !value || Number(limit) === 0 || Number(value) > Number(limit)
      ? undefined
      : this.props.t('Stop price must be greater than {{limit}}.', {limit: numberWithCommas(limit)});

  stopPriceMustBeLessThan = limit => value =>
    !value || limit === 'inf' || Number(value) < Number(limit)
      ? undefined
      : this.props.t('Stop price must be less than {{limit}}.', {limit: numberWithCommas(limit)});

  stopPriceValidator = (side, orders, pairName) => {
    const {pairs} = this.state;
    const {lastPrice} = pairs[pairName];
    if (side === "BUY") {
      return this.stopPriceMustBeGreaterThan(max([
        Number(lastPrice),
        orders && orders.length ? Number(orders[0].price) : Number(lastPrice)
      ]));
    } else {
      return this.stopPriceMustBeLessThan(min([
        Number(lastPrice),
        orders && orders.length ? Number(orders[0].price) : Number(lastPrice)
      ]));
    }
  };

  getMaxAmount = (values, orders) => {
    const {side, type, price, pairName} = values;
    const {pairs, wallets} = this.state;
    const symbols = pairName.split('/');
    const pair = pairs[pairName];
    const baseBalance = wallets[symbols[0]].balance;
    const quoteBalance = wallets[symbols[1]].balance;
    if (side === 'SELL') {
      return baseBalance;
    } else {
      if (type !== 'MARKET') {
        if (price) {
          return Number(quoteBalance) / Number(price);
        }
      } else {
        const totalPrice = Number(quoteBalance) * 0.98 >= Number(pair.minCost) ? Number(quoteBalance) * 0.98 : Number(quoteBalance);
        const {amount} = this.computeMarketAmount(orders, totalPrice);
        return amount;
      }
    }
  };

  computeMarketAmount = (orders, totalPrice) => {
    let remainedTotalPrice = totalPrice;
    let amount = 0;
    let i = 0;
    if (!orders || !orders.length) {
      return {amount: 0, totalPrice: 0};
    }
    while (remainedTotalPrice) {
      if (!orders[i]) {
        break;
      }
      const orderTotalPrice = Number(orders[i].amount) * Number(orders[i].price);
      if (orderTotalPrice <= remainedTotalPrice) {
        amount += Number(orders[i].amount);
        remainedTotalPrice -= orderTotalPrice;
      } else {
        amount += remainedTotalPrice / Number(orders[i].price);
        remainedTotalPrice = 0;
      }
      i++;
    }
    return {
      amount: !remainedTotalPrice ? amount : 0,
      totalPrice: Number(totalPrice) - remainedTotalPrice,
    };
  };

  onClickBalance = (type, pair, price, orders, input) => {
    const {tradeAmountDecimals} = pair;
    const amount = this.getMaxBuyAmount(type, pair, price, orders);
    input.onChange(this.fixedToWithoutRound(amount, tradeAmountDecimals));
  };

  getMaxBuyAmount = (type, pair, price, orders) => {
    const {wallets} = this.state;
    const quoteToken = pair.name.split('/')[1];
    if (wallets && wallets[quoteToken]) {
      if (type === 'LIMIT') {
        if (price) {
          return Number(wallets[quoteToken].balance) / Number(price);
        }
      } else {
        const price = Number(wallets[quoteToken].balance) * 0.98 >= Number(pair.minCost) ?
          Number(wallets[quoteToken].balance) * 0.98 : Number(wallets[quoteToken].balance)
        const {amount} = this.computeMarketAmount(orders, price);
        return amount;
      }
    }
    return undefined;
  };

  render() {
    const {t, tradeFee} = this.props;
    const {orderbook, wallets, pairs} = this.state;
    let sellOrders, buyOrders;
    if (this.form && orderbook) {
      sellOrders = orderbook.filter(order => order.side.toUpperCase() === 'SELL');
      buyOrders = orderbook.filter(order => order.side.toUpperCase() === 'BUY');
    }
    const {tokens} = store.getState().general;
    return(
      <FinalForm
        initialValues={{
          side: 'BUY',
          type: 'LIMIT',
        }}
        validate={this.validate}
        onSubmit={this.placeOrder}
        render={({form, handleSubmit, submitting, hasValidationErrors, values, errors}) => {
          this.form = form;
          let pair, orders, symbols, locales;
          if (values.pairName) {
            pair = pairs[values.pairName];
            symbols = values.pairName.split('/');
            locales = pair.localeName.split('/');
            orders = values.side === 'BUY' ? sellOrders : buyOrders;
          }
          return (
            <Form onSubmit={handleSubmit}>
              <Row className="mb-3">
                <Col>
                  <Field name="pairName" validate={required}>
                    {({input}) => (
                      <Select
                        {...input}
                        className="w-100"
                        styles={selectStyles}
                        value={find(this.pairOptions, {value: input.value})}
                        onChange={selected => {this.onSelectPair(selected, input);}}
                        options={this.pairOptions}
                        placeholder={t('Select Pair')}
                      />
                    )}
                  </Field>
                </Col>
              </Row>
              {values.pairName &&
                <div>
                  <Row className="mb-3">
                    <Col>
                      <Field name="side" validate={required}>
                        {({input}) => (
                          <Select
                            {...input}
                            className="w-100"
                            styles={selectStyles}
                            value={find(this.sideOptions, {value: input.value})}
                            onChange={selected => {
                              this.onSelectSide(selected);
                            }}
                            options={this.sideOptions}
                            placeholder={t('Select Pair')}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <Field name="type" validate={required}>
                        {({input}) => (
                          <Select
                            {...input}
                            className="w-100"
                            styles={selectStyles}
                            value={find(this.typeOptions, {value: input.value})}
                            onChange={selected => {
                              input.onChange(selected.value);
                            }}
                            options={this.typeOptions}
                            placeholder={t('Select')}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>
                  {values.type === 'STOP_LIMIT' &&
                    <Row className="mb-1">
                      <Col>
                        <Field
                          name="stopPrice"
                          validate={composeValidators(
                            mustBeNumber,
                            this.stopPriceValidator(
                              values.side,
                              values.side === 'BUY' ? buyOrders : sellOrders,
                              values.pairName
                            )
                          )}>
                          {({input, meta}) => (
                            <FormGroup>
                              <InputGroup>
                                <InputGroupAddon addonType='prepend'>
                                  <InputGroupText
                                    className='d-flex align-items-center justify-content-center'
                                    style={{minWidth: 60, fontSize: 11}}>
                                    {t('Stop Price')}
                                  </InputGroupText>
                                </InputGroupAddon>
                                <Input
                                  {...input}
                                  id="stopPrice"
                                  value={numberWithCommas(input.value)}
                                  invalid={meta.invalid && !!input.value}
                                  onChange={e => {input.onChange(this.fixedToWithoutRound(stringToNumber(e.target.value), pair.priceDecimals));}}
                                />
                                <InputGroupAddon addonType='append'>
                                  <InputGroupText
                                    className="border-right-0 d-flex align-items-center justify-content-center"
                                    style={{minWidth: 85, fontSize: 11}}>
                                    {locales[1]}
                                  </InputGroupText>
                                </InputGroupAddon>
                              </InputGroup>
                            </FormGroup>
                          )}
                        </Field>
                      </Col>
                    </Row>
                  }
                  {values.type !== 'MARKET' &&
                    <Row className="mb-1">
                      <Col>
                        <Field name="price" validate={mustBeNumber}>
                          {({input, meta}) => (
                            <FormGroup>
                              {orders && !!orders.length && values.type !== 'STOP_LIMIT' &&
                                <div className='d-flex flex-row align-items-center mb-2'>
                                  <div className='text-muted font-weight-300 pr-3'>{t('Best Ask')}:</div>
                                  <div
                                    className='impressive-value cursor-pointer'
                                    onClick={() => {input.onChange(this.fixedToWithoutRound(orders[0].price, pair.priceDecimals));}}>
                                    {`${numberWithCommas(this.fixedToWithoutRound(orders[0].price, pair.priceDecimals))} ${locales[1]}`}
                                  </div>
                                </div>
                              }
                              <InputGroup>
                                <InputGroupAddon addonType='prepend'>
                                  <InputGroupText
                                    className='d-flex align-items-center justify-content-center'
                                    style={{minWidth: 60, fontSize: 11}}>
                                    {t('Price')}
                                  </InputGroupText>
                                </InputGroupAddon>
                                <Input
                                  {...input}
                                  id="price"
                                  value={numberWithCommas(input.value)}
                                  invalid={meta.invalid && !!input.value}
                                  onChange={e => {input.onChange(this.fixedToWithoutRound(stringToNumber(e.target.value), pair.priceDecimals));}}
                                />
                                <InputGroupAddon addonType='append'>
                                  <InputGroupText
                                    className="border-right-0 d-flex align-items-center justify-content-center"
                                    style={{minWidth: 85, fontSize: 11}}>
                                    {locales[1]}
                                  </InputGroupText>
                                </InputGroupAddon>
                              </InputGroup>
                            </FormGroup>
                          )}
                        </Field>
                      </Col>
                    </Row>
                  }
                  <Row className="mb-4">
                    <Col>
                      <Field name="amount" validate={mustBeNumber}>
                        {({input, meta}) => (
                          <FormGroup>
                            {values.side === "BUY" && !!wallets && !!wallets[symbols[1]] &&
                              <div className='d-flex flex-row align-items-center mb-2'>
                                <div className='text-muted font-weight-300 pr-3'>{t('Balance')}:</div>
                                <div
                                  className='impressive-value cursor-pointer'
                                  onClick={() => {this.onClickBalance(values.type, pair, values.price, orders, input);}}>
                                  {`${numberWithCommas(this.fixedToWithoutRound(Number(wallets[symbols[1]].balance), pair.priceDecimals))} ${locales[1]}`}
                                </div>
                              </div>
                            }
                            {values.side === "SELL" && !!wallets && !!wallets[symbols[0]] &&
                              <div className='d-flex flex-row align-items-center mb-2'>
                                <div className='text-muted font-weight-300 pr-3'>{t('Balance')}:</div>
                                <div
                                  className='impressive-value cursor-pointer'
                                  onClick={() => {input.onChange(this.fixedToWithoutRound(wallets[symbols[0]].balance, pair.tradeAmountDecimals))}}>
                                  {`${numberWithCommas(this.fixedToWithoutRound(Number(wallets[symbols[0]].balance), pair.tradeAmountDecimals))} ${locales[0]}`}
                                </div>
                              </div>
                            }
                            <div className="mb-1">
                              <InputGroup style={{marginBottom: 2}}>
                                <InputGroupAddon addonType="prepend">
                                  <InputGroupText
                                    className='d-flex align-items-center justify-content-center'
                                    style={{minWidth: 55, fontSize: 11}}>
                                    {t('Amount')}
                                  </InputGroupText>
                                </InputGroupAddon>
                                <Input
                                  {...input}
                                  id="amount"
                                  value={numberWithCommas(input.value)}
                                  onChange={e => {input.onChange(this.fixedToWithoutRound(stringToNumber(e.target.value), pair.tradeAmountDecimals));}}
                                  invalid={meta.invalid && !!input.value}
                                />
                                <InputGroupAddon addonType='append'>
                                  <InputGroupText
                                    className="border-right-0 d-flex align-items-center justify-content-center"
                                    style={{minWidth: 85, fontSize: 11}}>
                                    {locales[0]}
                                  </InputGroupText>
                                </InputGroupAddon>
                              </InputGroup>
                              <PercentButtonGroup
                                amount={this.getMaxAmount(values, orders)}
                                onChoosePercentage={result => {
                                  input.onChange(this.fixedToWithoutRound(stringToNumber(result), pair.tradeAmountDecimals));
                                }}
                              />
                            </div>
                          </FormGroup>
                        )}
                      </Field>
                    </Col>
                  </Row>
                  <OrderDetails
                    values={values}
                    locales={locales}
                    tradeFee={tradeFee}
                    pairName={values.pairName}
                    priceDecimals={pair.priceDecimals}
                    hasValidationErrors={hasValidationErrors}
                    marketType={find(tokens, {symbol: symbols[1]}).tokenType}
                    form={this.form}
                    orders={orders}
                  />
                  <OrderErrors validationErrors={errors}/>
                  <div className="d-flex align-items-center justify-content-center mt-4">
                    <LoaderButton
                      loading={submitting}
                      onSubmit={handleSubmit}
                      className="w-70 text-center btn-lg"
                      disabled={hasValidationErrors || submitting || !values.amount || (values.type !== 'MARKET' && !values.price) || (values.type === 'STOP_LIMIT' && !values.stopPrice)}
                      color={values.side === "BUY" ? "success" : "danger"}>
                      {values.side === 'BUY' ? t('Buy') : t('Sell')}
                    </LoaderButton>
                  </div>
                </div>
              }
            </Form>
          );
        }}
      />
    );
  }
}

export default withTranslation()(PlaceOrderForm);
